import {renderMarkdown} from '../../../../packages/shared/markdown.js';
import '@/styles/notifications-layout.css';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { resolveMediaUrl } from '@/lib/assetUrl';
import {
  cleanNotificationHtmlClient,
  formatNotificationDate,
  useNotification,
  useToggleNotificationLike,
} from '@/hooks/useNotifications';

export function NotificationDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { t } = useLocale();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: notification, isLoading, error } = useNotification(Number.isNaN(id) ? null : id);
  const toggleLike = useToggleNotificationLike();
  const [liked, setLiked] = useState<number | null>(null);

  useEffect(() => {
    if (notification) {
      setLiked(notification.liked ?? null);
    }
  }, [notification]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if(notification?.content_format==='markdown'){renderMarkdown(el,notification.content??'');return;}
    el.querySelectorAll('[data-start], [data-end]').forEach((node) => {
      node.removeAttribute('data-start');
      node.removeAttribute('data-end');
    });
  }, [notification?.content,notification?.content_format]);

  const handleToggleLike = () => {
    if (!id || Number.isNaN(id)) return;
    toggleLike.mutate(id, {
      onSuccess: (newLiked) => setLiked(newLiked),
      onError: () => {
        window.alert(t('notifications_vote_error'));
      },
    });
  };

  if (Number.isNaN(id) || id <= 0) {
    navigate('/notifications', { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <div className="container-ingame notifications-page">
        <div className="notification-detail-container">
          <SimpleMenu title={t('notifications_title')} />
          <div style={{ padding: 20, flex: 1, overflowY: 'auto', width: '100%', color: '#999' }}>
            {t('notifications_loading')}
          </div>
        </div>
      </div>
    );
  }

  if (error || !notification) {
    return (
      <div className="container-ingame notifications-page">
        <div className="notification-detail-container">
          <SimpleMenu title={t('notifications_title')} />
          <div className="notifications-body">
            <div
              className="alert alert-danger"
              style={{
                marginBottom: 20,
                padding: 15,
                background: '#c00',
                color: '#fff',
                borderRadius: 5,
              }}
            >
              {t('notifications_not_found')}
            </div>
            <Link to="/notifications" className="btn btn-secondary">
              <i className="ph-light ph-arrow-left" /> {t('notifications_back_to_notifications')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const contentHtml = notification.content_format!=='markdown' && notification.content
    ? cleanNotificationHtmlClient(notification.content)
    : '';

  return (
    <div className="container-ingame notifications-page">
      <div className="notification-detail-container">
        <SimpleMenu title={t('notifications_title')} />

        <div className="notifications-body">
          <div className="notification-detail-content">
            {notification.banner_url ? (
              <div className="notification-detail-banner">
                <img
                  src={resolveMediaUrl(notification.banner_url)}
                  alt="Banner"
                  className="img-fluid"
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 0 }}
                />
              </div>
            ) : null}

            <div className="notification-detail-header">
              <div className="notification-detail-icon">
                {notification.icon_class ? (
                  <i className={notification.icon_class} style={{ fontSize: 28 }} />
                ) : notification.icon_url ? (
                  <img
                    src={resolveMediaUrl(notification.icon_url)}
                    alt=""
                    style={{ width: 32, height: 32 }}
                  />
                ) : (
                  <i className="ph-light ph-bell" style={{ fontSize: 28 }} />
                )}
              </div>
              <div className="notification-detail-title-section">
                <h1 className="notification-detail-title">{notification.title}</h1>
                <span className="notification-detail-type">
                  {notification.type_name ?? t('notifications_no_type')}
                </span>
              </div>
            </div>

            <div
              className="notification-detail-body"
              id="notification-content"
              ref={contentRef}
              dangerouslySetInnerHTML={notification.content_format==='markdown'?undefined:{ __html: contentHtml }}
            />

            <div className="notification-detail-dates">
              <small className="text-muted">
                {t('notifications_published')} {formatNotificationDate(notification.publish_at)}
                {notification.expires_at ? (
                  <>
                    {' '}
                    | {t('notifications_expires')}{' '}
                    {formatNotificationDate(notification.expires_at)}
                  </>
                ) : null}
              </small>
            </div>

            <div className="notification-detail-actions">
              <button
                type="button"
                id={`like-btn-${notification.id}`}
                className={`notification-like-btn${liked === 1 ? ' liked' : ''}`}
                disabled={toggleLike.isPending}
                onClick={handleToggleLike}
              >
                <i className="ph-light ph-thumbs-up" />
                <span>{t('notifications_like')}</span>
              </button>
              <button
                type="button"
                id={`dislike-btn-${notification.id}`}
                className={`notification-dislike-btn${liked === 0 ? ' disliked' : ''}`}
                disabled={toggleLike.isPending}
                onClick={handleToggleLike}
              >
                <i className="ph-light ph-thumbs-down" />
                <span>{t('notifications_dislike')}</span>
              </button>
            </div>

            <div className="notification-detail-back">
              <Link to="/notifications" className="btn btn-secondary">
                <i className="ph-light ph-arrow-left" /> {t('notifications_back_to_notifications')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

